import React, { useEffect, useState } from 'react';
import i18n from '../../../../../i18n';

import { makeStyles } from '@material-ui/core/styles';
import { authStoreIdSelector } from '../../../../state/auth/selectors';
import { useSelector } from 'react-redux';
import { Text, TextTypography } from '../../../../components/Text/Text';
import { getRequest } from '../../../../core/axios/axios';
import { makeUrl } from '../../../../core/utils/utils';
import { Endpoint } from '../../../../Endpoint';

interface StatisticElementParams {
    text: string;
    number: number;
    numberColor: string;
}

interface StatisticData {
    calls: number;
    categoryFeedShow: number;
    clientIp: string;
    createDate: Date;
    feedShow: number;
    mapFeedShow: number;
    productSearchShow: number;
    searchShow: number;
    storeEntrance: number;
    storeId: string;
    topFeedShow: number;
    userAgent: string;
}

const useStyles = makeStyles(theme => ({
    container: {
        width: '100%',
        background: 'rgba(15, 24, 37, 0.05)',
        padding: theme.spacing(3),
        borderRadius: 20,
    },
    row: {
        marginLeft: -theme.spacing(1),
        marginRight: -theme.spacing(1),
        display: 'flex',
        flexDirection: 'row',
        [theme.breakpoints.down(768)]: {
            flexDirection: 'column',
        },
    },
    title: {
        paddingBottom: theme.spacing(3),
    },
    element: {
        flex: 1,
        background: '#fff',
        borderRadius: 20,
        padding: theme.spacing(2),
        margin: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        [theme.breakpoints.down(375)]: {
            marginTop: theme.spacing(0.5),
            marginBottom: theme.spacing(0.5),
        },
    },
    elementText: {
        letterSpacing: -0.5,
    },
    number: {
        fontSize: theme.spacing(4),
        lineHeight: 1.5,
        paddingLeft: theme.spacing(0.5),
        fontWeight: 'bold',
        letterSpacing: -2,
        [theme.breakpoints.down(640)]: {
            fontSize: theme.spacing(3),
        },
    },
    elements: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        [theme.breakpoints.down(480)]: {
            flexDirection: 'column',
        },
    },
    // Remove when metrics will be fixed
    relative: {
        position: 'relative',
    },
    errorWithStats: {
        borderRadius: 20,
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        backgroundColor: '#000000b3',
        zIndex: 1,
    },
    errorText: {
        fontSize: '1.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 20,
        padding: 10,
        color: '#212529',
        backgroundColor: '#ffd43b',
        opacity: '100%',
        margin: 10,
        [theme.breakpoints.down(768)]: {
            transform: 'none',
        },
    },
}));

function countViews(stats: StatisticData): number {
    return (
        stats.topFeedShow +
        stats.feedShow +
        stats.categoryFeedShow +
        stats.topFeedShow +
        stats.productSearchShow +
        stats.searchShow +
        stats.storeEntrance +
        stats.calls
    );
}

export const StatisticElement = (params: StatisticElementParams) => {
    const classes = useStyles();

    return (
        <div className={classes.element}>
            <div className={classes.elementText}>
                <Text typography={TextTypography.CAPTION}>{params.text}</Text>
            </div>
            <div className={classes.number} style={{ color: params.numberColor }}>
                {params.number}
            </div>
        </div>
    );
};

export const StatisticWidget = () => {
    const classes = useStyles();
    const storeId: string = useSelector(authStoreIdSelector);
    const [statistic, setStatistic] = useState<StatisticData>(null);
    const [allViews, setAllViews] = useState(0);
    const { t } = i18n.useTranslation();
    useEffect(() => {
        const url = makeUrl(Endpoint.VENDOR_GET_STORE_STATISTIC, {}, { storeId });
        getRequest(url)
            .then((response: { data: StatisticData }) => {
                setStatistic(response.data);
                setAllViews(countViews(response.data));
            })
            .catch(console.log);
    }, [storeId]);

    if (!statistic || allViews < 10) {
        return null;
    }

    return (
        <div className={classes.relative}>
            {/*            <div className={classes.errorWithStats}>
                <div className={classes.errorText}>{t('common:errorInStatistic')}</div>
            </div>*/}
            <div className={classes.container}>
                <div className={classes.title}>
                    <Text typography={TextTypography.LEAD_ACCENT}>{t('vendor:dashboard:statisticWidget:title')}</Text>
                </div>
                <div className={classes.row}>
                    <div className={classes.elements}>
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:searchShow')}
                            number={statistic.searchShow}
                            numberColor={'#75C345'}
                        />
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:productSearchShow')}
                            number={statistic.productSearchShow}
                            numberColor={'#FFA310'}
                        />
                    </div>
                    <div className={classes.elements}>
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:storeEntrance')}
                            number={statistic.storeEntrance}
                            numberColor={'#2697FF'}
                        />
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:calls')}
                            number={statistic.calls}
                            numberColor={'#A649FD'}
                        />
                    </div>
                </div>
                <div className={classes.row}>
                    <div className={classes.elements}>
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:mapFeedShow')}
                            number={statistic.mapFeedShow}
                            numberColor={'#694A85'}
                        />
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:topFeedShow')}
                            number={statistic.topFeedShow}
                            numberColor={'#75C345'}
                        />
                    </div>
                    <div className={classes.elements}>
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:categoryFeedShow')}
                            number={statistic.categoryFeedShow}
                            numberColor={'#FF7200'}
                        />
                        <StatisticElement
                            text={t('vendor:dashboard:statisticWidget:feedShow')}
                            number={statistic.feedShow}
                            numberColor={'#2697FF'}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
